import { formatCurrency, WEEKDAY_LABELS } from '@crm/shared';
import { Badge, Button, Card, CardContent, Input, Select, Spinner } from '@crm/ui';
import { BadgePercent, ChevronRight, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { ConfirmDialog, EmptyState, PageHeader, Pagination } from '@/components';
import { useDebounce, useDocumentTitle } from '@/hooks';
import { useCan } from '@/modules/auth/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import { type CashbackRule, useCashbackRules, useDeleteCashbackRule } from '../api';
import { RuleDialog } from '../components/rule-dialog';

/** Minutos desde a meia-noite viram "13:30" para leitura. */
function timeLabel(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

/** A regra em uma frase: quando ela vale. */
function describe(rule: CashbackRule): string {
  const parts = [rule.serviceName ?? 'Todos os servicos'];

  if (rule.unitName) parts.push(rule.unitName);
  if (rule.weekdays.length > 0) {
    parts.push(rule.weekdays.map((day) => WEEKDAY_LABELS[day]).join(', '));
  }
  if (rule.startMinutes !== null || rule.endMinutes !== null) {
    parts.push(
      `${rule.startMinutes === null ? 'ate' : timeLabel(rule.startMinutes)}${
        rule.endMinutes === null ? '' : ` - ${timeLabel(rule.endMinutes)}`
      }`,
    );
  }
  if (rule.minAmount) parts.push(`a partir de ${formatCurrency(rule.minAmount)}`);

  parts.push(`credito vale ${rule.validityDays} dias`);

  return parts.join(' - ');
}

export function CashbackRulesPage() {
  useDocumentTitle('Regras de cashback');

  const canWrite = useCan('finance', 'WRITE');

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const debouncedSearch = useDebounce(search, 300);

  const rules = useCashbackRules({
    page,
    perPage,
    status,
    search: debouncedSearch || undefined,
  });
  const deleteRule = useDeleteCashbackRule();

  const [editing, setEditing] = useState<CashbackRule | null>(null);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState<CashbackRule | null>(null);

  function openDialog(rule: CashbackRule | null) {
    setEditing(rule);
    setDialogOpen(true);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<BadgePercent />}
        title="Regras de cashback"
        description="Quanto do valor pago volta como credito para o cliente, e por quanto tempo."
        actions={
          canWrite ? (
            <Button onClick={() => openDialog(null)}>
              <Plus className="h-4 w-4" />
              <span>
                Nova<span className="hidden sm:inline"> regra</span>
              </span>
            </Button>
          ) : null
        }
      />

      {deleteRule.error ? (
        <p className="text-sm text-destructive">{toErrorMessage(deleteRule.error)}</p>
      ) : null}

      <Card variant="flush">
        <CardContent className="space-y-6">
          {/* Filtro e lista no mesmo quadro: sao a mesma ferramenta, e o espaco
            entre eles ja diz onde um acaba e o outro comeca. */}
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              placeholder="Buscar pelo nome"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
            <Select
              aria-label="Situacao"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value as 'all' | 'active' | 'inactive');
                setPage(1);
              }}
            >
              <option value="all">Todas</option>
              <option value="active">Ativas</option>
              <option value="inactive">Inativas</option>
            </Select>
          </div>

          {rules.isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner className="h-6 w-6 text-muted-foreground" />
            </div>
          ) : rules.isError ? (
            <p className="text-sm text-destructive">{toErrorMessage(rules.error)}</p>
          ) : rules.data && rules.data.data.length > 0 ? (
            <ul className="divide-y divide-border">
              {rules.data.data.map((rule) => (
                <li
                  key={rule.id}
                  className="flex items-center gap-2 py-3 first:pt-0 last:pb-0 sm:gap-4"
                >
                  {/* A linha toda abre a regra: no celular o alvo e o registro
                      inteiro, nao um icone no canto. */}
                  <button
                    type="button"
                    className="min-w-0 flex-1 text-left"
                    onClick={() => openDialog(rule)}
                  >
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{rule.name}</span>
                      <Badge variant="secondary">{rule.percent}% de volta</Badge>
                      {rule.isActive ? null : <Badge variant="outline">Inativa</Badge>}
                    </span>
                    <span className="line-clamp-2 block text-xs text-muted-foreground">
                      {describe(rule)}
                    </span>
                  </button>

                  <ChevronRight
                    aria-hidden
                    className="h-5 w-5 shrink-0 text-muted-foreground sm:hidden"
                  />

                  {canWrite ? (
                    <div className="hidden shrink-0 items-center sm:flex sm:justify-end">
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Editar"
                        onClick={() => openDialog(rule)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Excluir"
                        onClick={() => setPendingRemoval(rule)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState icon={<BadgePercent />} title="Nenhuma regra de cashback" />
          )}

          <Pagination
            meta={rules.data?.meta}
            label="regras"
            onChange={setPage}
            perPage={perPage}
            onPerPageChange={(value) => {
              setPerPage(value);
              setPage(1);
            }}
          />
        </CardContent>
      </Card>

      <RuleDialog
        open={isDialogOpen}
        rule={editing}
        onClose={() => {
          setDialogOpen(false);
          setEditing(null);
        }}
      />

      <ConfirmDialog
        open={pendingRemoval !== null}
        title="Excluir regra"
        message={`A regra ${pendingRemoval?.name ?? ''} para de valer para os proximos atendimentos. Os creditos ja gerados ficam.`}
        loading={deleteRule.isPending}
        onClose={() => setPendingRemoval(null)}
        onConfirm={() => {
          if (pendingRemoval) deleteRule.mutate(pendingRemoval.id);
          setPendingRemoval(null);
        }}
      />
    </div>
  );
}
